<script setup lang="ts">
import { ref } from 'vue';

import {
  useBrowserStore,
  useMapStore,
  useRecipeStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppBasePlaceholder, AppFlicker, AppHeightTransition } from '@libs/ui';
import { TransportType } from '@libs/trip-planner-interfaces';

import { AppPlaceholderEventType } from '../../models';
import type { ItineraryUpsellNoTransportSegment } from '../../models';
import { useTripOverviewPageStore } from '../../stores';
import { TransportSelector } from '../../../../modules/tranposrtSelector';

import TransportBanner from './upsell/TransportBanner.vue';
import ItineraryTimeline from './ItineraryTimeline.vue';

const { t } = i18n.global;

interface ItineraryUpsellNoTransportSegmentProps {
  segment: ItineraryUpsellNoTransportSegment;
  isLoading?: boolean;
}

const props = defineProps<ItineraryUpsellNoTransportSegmentProps>();

const tripOverviewPageStore = useTripOverviewPageStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const mapStore = useMapStore();
const recipeStore = useRecipeStore();

const showAddFlightsBanner = ref<boolean>(false);

function selectTransport(transport: TransportType): void {
  tripOverviewPageStore.selectTransport(props.segment, transport);
}

function handleAction(actionType: AppPlaceholderEventType): void {
  switch (actionType) {
    case AppPlaceholderEventType.AddFlight:
      if (recipeStore.isSingleHotelRecipe) {
        showAddFlightsBanner.value = true;
      } else {
        selectTransport(TransportType.Flight);
      }

      break;
    case AppPlaceholderEventType.AddFerry:
      selectTransport(TransportType.Ferry);
      break;
  }
}
</script>

<template>
  <ItineraryTimeline :segment-type="props.segment.segmentType">
    <template #banner>
      <AppHeightTransition
        :show-key="showAddFlightsBanner ? 'banner' : 'timeline'"
      >
        <TransportBanner
          v-if="showAddFlightsBanner"
          class="transport-banner"
          :routes="tripOverviewPageStore.getAddFlightsModalRoutes()"
          :start-positon="mapStore.startingLocation"
          :end-position="mapStore.endLocation"
          :city="props.segment.city"
          :is-small-device="browserStore.isSmallerThan900px"
          @add-flight="selectTransport(TransportType.Flight)"
          @add-optimized-flights="
            tripOverviewPageStore.confirmAddFlightsModal($event)
          "
          @close="showAddFlightsBanner = false"
        />
        <AppBasePlaceholder
          v-else
          class="transport-placeholder"
          :title="
            t('overview_placeholder.transport.title', [props.segment.city])
          "
          :description="t('overview_placeholder.transport.description')"
          :is-small-device="browserStore.isSmallerThan900px"
          :icon-properties="{
            name: 'seatInCircle',
            color: 'color-brand-jungle',
          }"
          :actions="[
            {
              textToDisplay: t('placeholder.add_flight'),
              eventType: AppPlaceholderEventType.AddFlight,
            },
            {
              textToDisplay: t('placeholder.add_ferry'),
              eventType: AppPlaceholderEventType.AddFerry,
            },
          ]"
          :is-loading="props.isLoading"
          @action-clicked="handleAction($event as AppPlaceholderEventType)"
        >
          <template #actions>
            <AppFlicker
              v-if="props.isLoading"
              width="spacing-64"
              :height="
                browserStore.isSmallerThan900px ? 'font-size-s' : 'font-size-m'
              "
              :margin="{ ySpacing: 'spacing-4' }"
            />
            <TransportSelector
              v-else
              :transport-type="TransportType.None"
              :disabled="false"
              :initial-option="t('placeholder.more')"
              :has-user-selected-transport="false"
              :is-upsell-no-transport="true"
              :font-variant="
                browserStore.isSmallerThan900px
                  ? 'font-body-s-bold'
                  : 'font-body-m-bold'
              "
              simple-transport-options
              @select="tripOverviewPageStore.selectTransport(segment, $event)"
              @open="statsigEventsStore.logTripOverviewTransportSelectorOpened"
            />
          </template>
        </AppBasePlaceholder>
      </AppHeightTransition>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.transport-placeholder,
.transport-banner {
  @include for-less-or-equal-than-1199-px {
    margin-left: var(--spacing-8);
  }
  @include for-less-or-equal-than-599-px {
    margin-left: 0;
  }
}
</style>
